import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { MedicalServicesOutlined as ServiceIcon } from "@mui/icons-material";
import { ActionButton } from "../../atoms";
import { StatusPill } from "../ClinicRecordList";
import { useClinicTheme } from "../../../theme";
import { useAlert } from "../../../context/AlertProvider";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useClinicModule } from "../../../hooks/useClinicModule";
import useAuth from "../../../hooks/useAuth";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { formatMoney } from "../../../utils/money";
import { useSetAppointmentServiceMutation } from "../../../store/billingApi";
import AppointmentServiceFields from "./AppointmentServiceFields";

const idOf = (v) => (v && typeof v === "object" ? v._id : v) || "";

/**
 * Randevu detay drawer'ında hizmet / paket bölümü. Planlanmış randevuda
 * (appointments:update) değiştirilebilir; diğer durumlarda salt okunur.
 * Billing modülü kapalıysa ya da `services:read` yoksa çizilmez.
 */
export default function AppointmentBillingSection({ appointment, onChanged }) {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const { showSuccess, showError } = useAlert();
  const billingActive = useClinicModule("billing");
  const canServices = useHasPermission("services:read");
  const canUpdate = useHasPermission("appointments:update");
  const [save, { isLoading }] = useSetAppointmentServiceMutation();

  const [current, setCurrent] = useState(appointment);
  const [draft, setDraft] = useState({ serviceId: "", patientPackageId: "" });

  useEffect(() => {
    setCurrent(appointment);
    setDraft({ serviceId: idOf(appointment?.service), patientPackageId: idOf(appointment?.patientPackage) });
  }, [appointment]);

  if (!billingActive || !canServices || !current) return null;

  const status = String(current.status || "").toLowerCase();
  const editable = canUpdate && ["scheduled", "in_examination"].includes(status);
  const dirty =
    draft.serviceId !== idOf(current.service) || draft.patientPackageId !== idOf(current.patientPackage);

  const submit = async () => {
    try {
      const r = await save({
        token,
        appointmentId: current._id,
        serviceId: draft.serviceId || undefined,
        patientPackageId: draft.patientPackageId || undefined,
      }).unwrap();
      const next = r?.data?.appointment;
      if (next) setCurrent((c) => ({ ...c, ...next }));
      showSuccess("Randevunun hizmeti güncellendi");
      onChanged?.(next);
    } catch (e) {
      showError(e?.data?.message || "Hizmet güncellenemedi");
    }
  };

  return (
    <Box
      sx={{
        bgcolor: COLORS.white,
        borderRadius: 2.5,
        border: `1px solid ${COLORS.slate200}`,
        px: 2.5,
        py: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ServiceIcon sx={{ fontSize: 18, color: COLORS.slate500 }} />
        <Typography sx={{ fontSize: TYPO.label, fontWeight: 600, color: COLORS.slate500, flex: 1 }}>
          Hizmet
        </Typography>
        {current.patientPackage ? (
          <StatusPill
            label={status === "completed" ? "Paketten düşüldü" : "Paketten düşecek"}
            tone="primary"
            palette={palette}
          />
        ) : null}
      </Box>

      {editable ? (
        <>
          <AppointmentServiceFields
            token={token}
            patientId={idOf(current.patient)}
            serviceId={draft.serviceId}
            patientPackageId={draft.patientPackageId}
            onChange={({ serviceId, patientPackageId }) => setDraft({ serviceId, patientPackageId })}
          />
          {dirty ? (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <ActionButton
                variant="save"
                compact
                palette={palette}
                label="Hizmeti kaydet"
                loading={isLoading}
                onClick={submit}
              />
            </Box>
          ) : null}
        </>
      ) : (
        <Typography sx={{ fontSize: TYPO.body, color: current.serviceName ? COLORS.slate800 : COLORS.slate400 }}>
          {current.serviceName
            ? `${current.serviceName}${current.servicePrice > 0 && !current.patientPackage ? ` · ${formatMoney(current.servicePrice)}` : ""}`
            : "Hizmet seçilmedi"}
        </Typography>
      )}
    </Box>
  );
}
